import { ExpandAltOutlined, TrophyOutlined } from "@ant-design/icons";
import { Button, theme, Tooltip, Typography } from "antd";
import type { AlertProps } from "antd";
import { ReactNode, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { readStorage, writeStorage } from "@/lib/storage";

const POSITION_KEY = "eventBadgePosition";
// Default spot: bottom left, over the code panel of every editor layout.
const DEFAULT_OFFSET = 16;

interface Position {
    x: number;
    y: number;
}

const readPosition = (): Position | null => {
    const raw = readStorage(POSITION_KEY);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw) as Partial<Position>;
        return typeof parsed.x === "number" && typeof parsed.y === "number"
            ? { x: parsed.x, y: parsed.y }
            : null;
    } catch {
        return null;
    }
};

// Storage unavailable: the badge just returns to its default spot.
const writePosition = (position: Position) =>
    writeStorage(POSITION_KEY, JSON.stringify(position));

interface EventMiniBadgeProps {
    type: NonNullable<AlertProps["type"]>;
    /** Countdown or short status. */
    status: ReactNode;
    eventName: string;
    onExpand: () => void;
}

// The minimised competition banner: a small translucent badge floating over
// the editor, draggable so it never has to cover the code being edited.
export default function EventMiniBadge({
    type,
    status,
    eventName,
    onExpand,
}: EventMiniBadgeProps) {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const [position, setPosition] = useState<Position | null>(readPosition);
    const drag = useRef<{
        pointerId: number;
        offsetX: number;
        offsetY: number;
        moved: boolean;
    } | null>(null);

    const accent = {
        success: token.colorSuccess,
        warning: token.colorWarning,
        error: token.colorError,
        info: token.colorInfo,
    }[type];

    const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        if ((e.target as HTMLElement).closest("button")) return;
        const rect = e.currentTarget.getBoundingClientRect();
        drag.current = {
            pointerId: e.pointerId,
            offsetX: e.clientX - rect.left,
            offsetY: e.clientY - rect.top,
            moved: false,
        };
        e.currentTarget.setPointerCapture(e.pointerId);
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        const current = drag.current;
        if (!current || current.pointerId !== e.pointerId) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = Math.min(
            Math.max(0, e.clientX - current.offsetX),
            window.innerWidth - rect.width,
        );
        const y = Math.min(
            Math.max(0, e.clientY - current.offsetY),
            window.innerHeight - rect.height,
        );
        current.moved = true;
        setPosition({ x, y });
    };

    const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        const current = drag.current;
        if (!current || current.pointerId !== e.pointerId) return;
        drag.current = null;
        if (current.moved && position) writePosition(position);
    };

    // Keep a saved spot on screen if the window got smaller since.
    const placement: React.CSSProperties = position
        ? {
              left: `min(${position.x}px, calc(100vw - 80px))`,
              top: `min(${position.y}px, calc(100vh - 40px))`,
          }
        : { left: DEFAULT_OFFSET, bottom: DEFAULT_OFFSET };

    return (
        <div
            data-testid="event-mini-badge"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{
                position: "fixed",
                zIndex: 900,
                ...placement,
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "4px 4px 4px 10px",
                borderRadius: 999,
                borderLeft: `4px solid ${accent}`,
                background: "rgba(255, 255, 255, 0.72)",
                backdropFilter: "blur(6px)",
                WebkitBackdropFilter: "blur(6px)",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.15)",
                cursor: "grab",
                touchAction: "none",
                userSelect: "none",
            }}
        >
            <Tooltip title={eventName}>
                <TrophyOutlined style={{ color: accent }} />
            </Tooltip>
            <Typography.Text
                strong
                style={{
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                }}
            >
                {status}
            </Typography.Text>
            <Tooltip title={t("events.mode.expand")}>
                <Button
                    type="text"
                    size="small"
                    shape="circle"
                    icon={<ExpandAltOutlined />}
                    aria-label={t("events.mode.expand")}
                    onClick={onExpand}
                />
            </Tooltip>
        </div>
    );
}
